import waitTask from '@/common/utils/waitTask';
import CommonError from '@/common/utils/errors/CommonError';
import useAbgwStore from '@/common/store/pinia/abgw';
import { TaskState } from '@schema';
import getClusterId from '@/common/store/pinia/storageCluster/getClusterId';

export const ABGW_GEO_REPLICATION_NO_REPLICATION = 'no-replication';
export const ABGW_GEO_REPLICATION_MASTER_MODE = 'master';
export const ABGW_GEO_REPLICATION_SLAVE_MODE = 'slave';

export const ABGW_GEO_REPLICATION_STATUS_ESTABLISHED = 'established';
export const ABGW_GEO_REPLICATION_STATUS_ESTABLISHING = 'establishing';
export const ABGW_GEO_REPLICATION_STATUS_DRAFT = 'draft';
export const ABGW_GEO_REPLICATION_STATUS_SET_UP = 'set_up';
export const ABGW_GEO_REPLICATION_STATUS_FAILED_TO_DISABLE = 'failed_to_disable';
export const ABGW_GEO_REPLICATION_STATUS_FAILED_TO_ESTABLISH = 'failed_to_establish';
export const ABGW_GEO_REPLICATION_STATUS_FAILED_TO_CANCEL = 'failed_to_cancel';
export const ABGW_GEO_REPLICATION_STATUS_FAILED_SET_UP = 'failed_to_set_up';
export const ABGW_GEO_REPLICATION_STATUS_FAILED_TO_PROMOTE = 'failed_to_promote';
export const ABGW_GEO_REPLICATION_STATUS_FAILED_TO_UPDATE = 'failed_to_update';

// TODO: use actual task name
const taskName = 'abgw-geo-replication';

const getInitialState = () => ({
  loading: false,
  config: { },
  nodes: []
});

export default {
  name: 'abgw-geo-replication',
  namespaced: true,
  state: getInitialState,
  getters: {
    config: state => state.config,
    getConfigState: state => state.config?.role || ABGW_GEO_REPLICATION_NO_REPLICATION,
    isLoading: state => state.loading,
    abgwPublicAddresses: (state, getters, rootState, rootGetters) => rootGetters['locations/abgwNodes']
        .flatMap(node => node.ipAddresses.abgw_public)
  },
  mutations: {
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_CONFIG(state, config) {
      state.config = config;
    },
    SET_NODES(state, nodes) {
      state.nodes = nodes;
    },
    FLUSH_STATE(state) {
      Object.assign(state, getInitialState());
    },
  },
  actions: {
    FETCH({ commit }) {
      commit('SET_LOADING', true);

      return getClusterId()
        .then(clusterId => this.$api.get(`/api/v2/${clusterId}/abgw/geo-replication/`))
        .then(({ data }) => {
          commit('SET_CONFIG', data);
          commit('SET_LOADING', false);
        });
    },
    FETCH_ADDRESSES({ dispatch }) {
      return dispatch('locations/FETCH_LOCATIONS', undefined, { root: true });
    },
    RUN_CUSTOM_ACTION(_, { data, path, method = 'post' }) {
      return getClusterId()
        .then(clusterId => this.$api[method](`/api/v2/${clusterId}/abgw/geo-replication/${path}/`, { event: taskName, ...data }))
        .then(waitTask);
    },
    SET_UP_MASTER({ dispatch }, data) {
      return dispatch('RUN_CUSTOM_ACTION', { data, path: 'master/set-up' });
    },
    SET_UP_SLAVE({ dispatch, getters }, { file }) {
      return getClusterId()
        .then(clusterId => this.$api.upload(`/api/v2/${clusterId}/abgw/geo-replication/slave/set-up/`, file, {
          method: 'post',
          headers: {
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'application/octet-stream',
            'x-event': taskName
          },
          uploadTimeout: 60000
        }))
        .then(waitTask)
        .then(() => dispatch('FETCH'))
        .then(() => {
          if (getters.config.state === ABGW_GEO_REPLICATION_STATUS_FAILED_SET_UP) {
            throw new CommonError(getters.config.last_error);
          }
        });
    },
    UPDATE_CERT(_, { raw }) {
      return getClusterId()
        .then(clusterId => this.$api.upload(`/api/v2/${clusterId}/abgw/geo-replication/slave/update-certificates/`, raw, {
          method: 'post',
          headers: {
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'application/octet-stream',
            'x-event': taskName
          },
          uploadTimeout: 60000
        }))
        .then(waitTask);
    },
    CANCEL({ dispatch }) {
      return dispatch('RUN_CUSTOM_ACTION', { path: 'master/cancel' });
    },
    ESTABLISH({ dispatch }) {
      return dispatch('RUN_CUSTOM_ACTION', { path: 'master/establish' });
    },
    DISABLE({ dispatch }) {
      return dispatch('RUN_CUSTOM_ACTION', { path: 'master/disable' });
    },
    PROMOTE_TO_MASTER({ dispatch }) {
      return dispatch('RUN_CUSTOM_ACTION', { path: 'slave/promote-to-master' });
    },
    CANCEL_SLAVE({ dispatch }) {
      return dispatch('RUN_CUSTOM_ACTION', { path: 'slave/cancel' });
    },
    FETCH_CONFIG() {
      return getClusterId()
        .then(clusterId => this.$api.get(`/api/v2/${clusterId}/abgw/geo-replication/master/download-configs`));
    },
    socket_tasks({ dispatch }, { event, state }) {
      if (event === taskName && state === TaskState.SUCCESS) {
        dispatch('FETCH');
        const abgwStore = useAbgwStore();
        abgwStore.fetch();
      }
    },
  }
};
